import { doc, collection, writeBatch, updateDoc, deleteDoc, arrayUnion } from 'firebase/firestore';

import { useAuth } from '../contextProviders/AuthProvider';
import { useData } from '../contextProviders/DataProvider';
import { auth, firestore } from '../firebase';
import DefaultObjects from '../types/DefaultObjects';
import Membership from '../types/Membership';
import Org, { ORG_STATUSES } from '../types/Org';
import { denormalizeProfileFields } from '../utils/profileUtils';
import { CurrentDateTime } from '../utils/utils';

export const useOrgMutators = () => {
  const { isGod } = useAuth();
  const { userState } = useData();

  const addOrg = async (
    org: Org,
    isMember: boolean,
    isAdmin: boolean,
    isUserProfileUpdated = true
  ): Promise<Org | null> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found for auth id:', auth.currentUser?.uid);
      return null;
    }

    if (!isGod) {
      console.error('Permission denied. User id:', userState.userProfile.itemId);
      return null;
    }

    if (!org) {
      console.error('No org provided');
      return null;
    }

    try {
      const batch = writeBatch(firestore);

      // Create org - use provided itemId if available, otherwise generate a new one
      const orgRef = org.itemId
        ? doc(firestore, `org/${org.itemId}`)
        : doc(collection(firestore, 'org'));
      const newOrg = {
        ...org,
        creatorId: userState.userProfile.itemId,
        createTime: CurrentDateTime(),
        itemId: orgRef.id,
        status: ORG_STATUSES.ACTIVE,
      };
      batch.set(orgRef, newOrg);

      if (isUserProfileUpdated) {
        // Add membership to batch
        const membership: Membership = {
          ...DefaultObjects.Membership,
          isAdmin: isAdmin,
          isMember: isMember,
          itemId: userState.userProfile.itemId,
          name: userState.userProfile.name,
          orgId: orgRef.id,
          creatorId: userState.userProfile.itemId,
          createTime: CurrentDateTime(),
          ...denormalizeProfileFields(userState.userProfile),
        }
        const membershipRef = doc(firestore, `org/${orgRef.id}/membership/${userState.userProfile.itemId}`);
        batch.set(membershipRef, { ...membership });

        // Update profile in batch
        const profileRef = doc(firestore, 'profile', userState.userProfile.itemId);
        batch.update(profileRef, {
          orgIds: isMember ? arrayUnion(orgRef.id) : [],
          adminOrgIds: isAdmin ? arrayUnion(orgRef.id) : [],
        });
      }

      await batch.commit();
      return newOrg;
    } catch (error) {
      console.error('Error adding org:', error);
      return null;
    }
  };

  const deleteOrg = async (org: Org, isPermanent = false): Promise<void> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found for auth id:', auth.currentUser?.uid);
      return;
    }

    if (!isGod) {
      console.error('Permission denied. User id:', userState.userProfile.itemId);
      return;
    }

    if (!org.itemId) {
      console.error('Org itemId not found:', org.itemId);
      return;
    }

    try {
      const orgRef = doc(firestore, 'org/' + org.itemId);

      if (isPermanent) {
        await deleteDoc(orgRef);
      } else {
        await updateDoc(orgRef, { ...org, status: ORG_STATUSES.DELETED });
      }
    } catch (error) {
      console.error('Error deleting org:', error);
    }
  };

  const updateOrg = async (org: Partial<Org>, currentOrg?: Org): Promise<void> => {
    if (userState.status !== 'ready' || !userState.userProfile) {
      console.error('User profile not found for auth id:', auth.currentUser?.uid);
      return;
    }

    if (!org.itemId) {
      console.error('Org itemId not found:', org.itemId);
      return;
    }

    // For non-gods, validate and sanitize the update
    let updateData: Partial<Org> = { ...org };

    if (!isGod) {
      // Check if trying to update a deleted org
      if (currentOrg?.status === ORG_STATUSES.DELETED) {
        throw new Error('Cannot update a deleted organization');
      }

      // Remove disallowed fields for non-gods
      const { itemId: _itemId, createTime: _createTime, creatorId: _creatorId, catchAllLeagueId: _catchAllLeagueId, catchAllSeasonId: _catchAllSeasonId, ...allowedFields } = updateData;
      updateData = allowedFields;

      // Validate status changes - only ARCHIVED or DELETED allowed
      if ('status' in updateData && updateData.status !== undefined) {
        if (updateData.status !== ORG_STATUSES.ARCHIVED && updateData.status !== ORG_STATUSES.DELETED) {
          throw new Error('Status can only be changed to ARCHIVED or DELETED');
        }
      }
    }

    try {
      const orgRef = doc(firestore, 'org/' + org.itemId);
      await updateDoc(orgRef, { ...updateData });
    } catch (error) {
      console.error('Error updating org:', error);
      throw error;
    }
  };

  return {
    addOrg,
    deleteOrg,
    updateOrg,
  };
};